import React, { useMemo } from 'react'
import dynamic from 'next/dynamic'
// import { VectorSquare } from '@emotion-icons/fa-solid/VectorSquare'
import { Info } from '@emotion-icons/fa-solid/Info'
import { Insight, InsightComponentProps } from 'src/components/insights/types'
import { FullScreenLoading } from 'src/components/full-screen-loading'
import { Colors } from 'src/components/theme'
import { LocationVisitsIcon } from 'src/components/insights/icons/location-visits'
import InsightLocked from 'src/components/insight-locked'
import { useTier } from 'src/hooks/use-tier'
import { INSIGHTS_TIER } from 'src/constants/insights-tier'
import { getMMMYYYDate } from 'src/components/insights/custom-polygon-visits/utils'

import * as footTrafficTrendsAtoms from 'src/atoms/custom-polygon/foot-traffic-trends'
import * as searchAtoms from 'src/atoms/search'
import locationVisits1 from 'src/assets/images/insights-previews/location-visits/location_visits_1.gif'
import locationVisits1Placeholder from 'src/assets/images/insights-previews/location-visits/location_visits_1.png'
import locationVisits2 from 'src/assets/images/insights-previews/location-visits/location_visits_2.gif'
import locationVisits2Placeholder from 'src/assets/images/insights-previews/location-visits/location_visits_2.png'
import locationVisits3 from 'src/assets/images/insights-previews/location-visits/location_visits_3.gif'
import locationVisits3Placeholder from 'src/assets/images/insights-previews/location-visits/location_visits_3.png'
import { InsightContainer } from 'src/v2/views/owner-reveal/insights/container'
import { useRecoilValue } from 'recoil'

const CustomPolygonVisitsContent = dynamic(() => import('src/components/insights/custom-polygon-visits/content'), {
  loading: () => <FullScreenLoading />,
  ssr: false,
})

const CustomPolygonVisitsPage: React.FC<InsightComponentProps> = (props) => {
  const { insight } = props
  const meetsTier = useTier(INSIGHTS_TIER['Custom Polygon Visits'])
  const place = useRecoilValue(searchAtoms.googlePlace)
  const { start, end } = useRecoilValue(footTrafficTrendsAtoms.dateRange)

  const subtitle = useMemo(() => {
    if (!place) {
      return ''
    }
    const startDate = getMMMYYYDate(start)
    const endDate = getMMMYYYDate(end)
    return `${place.name} · ${place.formatted_address} · ${startDate} - ${endDate}`
  }, [place, start, end])

  return (
    <InsightContainer
      insight={insight}
      color={Colors.purpleDark}
      subtitle={subtitle}
      downloadFileName="Custom Polygon Visits">
      <React.Suspense fallback={<FullScreenLoading />}>
        {!meetsTier ? <InsightLocked insight={insight} /> : <CustomPolygonVisitsContent />}
      </React.Suspense>
    </InsightContainer>
  )
}

export const CustomPolygonVisits: Insight = {
  id: 'custom-polygon-visits',
  component: CustomPolygonVisitsPage,
  name: 'Location Visits (Mobile Data)',
  // TODO: temporarily changed icons until we solve the negotiations with unacast
  // nameIcon: VectorSquare,
  // nameIconColor: Colors.bgGreenDark,
  nameIcon: Info,
  nameIconColor: Colors.neutral400,
  icon: LocationVisitsIcon,
  supportedResultTypes: ['address', 'alphamap-location'],
  // supportedResultTypes: ['address', 'area', 'alphamap-location'],
  linkTooltipFile: 'insights-link-location-visits',
  tier: INSIGHTS_TIER['Location Visits'],
  paywall: {
    title: 'See Location Visits Reports in Action',
    description: `Location Visits isn't available on your current plan. However, you can view full functionality of this feature by viewing an unlocked location or upgrading your plan below.`,
    synopsis: [
      {
        image: locationVisits1.src,
        imagePlaceholder: locationVisits1Placeholder.src,
        title: 'Customer Insights Unlocked',
        description: 'Tap into customer visit patterns for smarter retail strategies and enhanced customer engagement.',
      },
      {
        image: locationVisits2.src,
        imagePlaceholder: locationVisits2Placeholder.src,
        title: 'Prime Site Selection',
        description:
          'Utilize visit trends data to identify high-value locations, ensuring profitable real estate investments and developments.',
      },
      {
        image: locationVisits3.src,
        imagePlaceholder: locationVisits3Placeholder.src,
        title: 'Strategic Marketing Advantage',
        description:
          'Leverage location-based insights for targeted marketing campaigns, placing your brand where your customers are.',
      },
    ],
  },
}
